import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import styled from "styled-components";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type OnChangeFn,
  type Row,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type TableMeta,
  type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDown, ChevronUp } from "lucide-react";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

type CellAlign = "left" | "center" | "right";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: CellAlign;
    stickyLeft?: boolean;
    stickyRight?: boolean;
  }
}

const ScrollContainer = styled.div`
  overflow: auto;
  width: 100%;
  height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: ${colors.backgroundLight};
  border-bottom: 1px solid ${colors.border};
`;

type CellSticky = "left" | "right";

const justifyFor = (align: CellAlign | undefined) =>
  align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start";

const stickyCss = (
  sticky: CellSticky | undefined,
  edge: boolean | undefined,
  zIndex: number,
) =>
  sticky
    ? `
    position: sticky;
    ${sticky === "right" ? "right: 0;" : ""}
    z-index: ${zIndex};
    background-color: ${colors.backgroundLight};
    ${sticky === "right" ? `border-left: 1px solid ${colors.border};` : ""}
    ${edge ? `border-right: 1px solid ${colors.border};` : ""}
  `
    : "";

const HeaderCell = styled.div<{
  $sortable?: boolean;
  $align?: CellAlign;
  $sticky?: CellSticky;
  $edge?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => justifyFor($align)};
  gap: ${spacing.x05}px;
  padding: ${({ $align }) =>
    $align === "center" ? `${spacing.x1}px 0` : `${spacing.x1}px ${spacing.x2}px`};
  font-weight: 600;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  cursor: ${({ $sortable }) => ($sortable ? "pointer" : "default")};
  user-select: none;
  box-sizing: border-box;
  flex-shrink: 0;

  ${({ $sticky, $edge }) => stickyCss($sticky, $edge, 3)}
`;

const BodyRow = styled.div<{
  $clickable?: boolean;
  $height: number;
  $translateY: number;
}>`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${colors.border};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${({ $height }) => $height}px;
  transform: translateY(${({ $translateY }) => $translateY}px);

  &:hover {
    background-color: ${colors.backgroundDark};
  }
`;

const BodyCell = styled.div<{
  $align?: CellAlign;
  $sticky?: CellSticky;
  $edge?: boolean;
}>`
  padding: ${({ $align }) => ($align === "center" ? "0" : `0 ${spacing.x1}px`)};
  font-size: ${fontSizes.regular}px;
  font-variant-numeric: tabular-nums;
  color: ${colors.text};
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
  flex-shrink: 0;
  text-align: ${({ $align }) => $align ?? "left"};
  ${({ $align }) =>
    $align === "center"
      ? "display: flex; align-items: center; justify-content: center;"
      : "text-overflow: ellipsis;"}

  & input {
    text-align: ${({ $align }) => $align ?? "left"};
  }

  ${({ $sticky, $edge, $align }) =>
    $sticky &&
    `
    ${stickyCss($sticky, $edge, 2)}
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: ${justifyFor($align)};

    ${BodyRow}:hover & {
      background-color: ${colors.backgroundDark};
    }
  `}
`;

export interface DataTableSubRows<TData, TSub> {
  get: (row: TData) => TSub[];
  getId: (sub: TSub) => string;
  height: number;
  renderCell: (columnId: string, row: TData, sub: TSub) => ReactNode;
}

interface DataTableProps<TData, TSub> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  rowCount: number;
  getRowId: (row: TData) => string;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  onLoadMore?: () => void;
  onRowClick?: (row: TData) => void;
  estimateRowHeight?: number;
  meta?: TableMeta<TData>;
  subRows?: DataTableSubRows<TData, TSub>;
}

export function DataTable<TData, TSub = never>({
  data,
  columns,
  rowCount,
  getRowId,
  sorting,
  onSortingChange,
  columnVisibility,
  onColumnVisibilityChange,
  rowSelection,
  onRowSelectionChange,
  onLoadMore,
  onRowClick,
  estimateRowHeight = 64,
  meta,
  subRows,
}: DataTableProps<TData, TSub>) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility, rowSelection },
    onSortingChange,
    onColumnVisibilityChange,
    onRowSelectionChange,
    getRowId,
    enableRowSelection: true,
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    rowCount,
    meta,
    getCoreRowModel: getCoreRowModel(),
  });

  const rows = table.getRowModel().rows;

  const items = useMemo(
    (): { row: Row<TData>; sub?: TSub }[] =>
      subRows
        ? rows.flatMap((row) => [
            { row },
            ...subRows.get(row.original).map((sub) => ({ row, sub })),
          ])
        : rows.map((row) => ({ row })),
    [rows, subRows],
  );

  const getItemKey = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item) {
        return index;
      }
      return item.sub !== undefined && subRows
        ? `${item.row.id}:${subRows.getId(item.sub)}`
        : item.row.id;
    },
    [items, subRows],
  );

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    getItemKey,
    estimateSize: (index) =>
      subRows && items[index]?.sub !== undefined
        ? subRows.height
        : estimateRowHeight,
    overscan: 12,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem || !onLoadMore) {
      return;
    }
    if (lastItem.index >= items.length - 1 && rows.length < rowCount) {
      onLoadMore();
    }
  }, [virtualItems, items.length, rows.length, rowCount, onLoadMore]);

  const totalWidth = table.getTotalSize();
  const stickyLeftEdgeId = table
    .getVisibleLeafColumns()
    .filter((column) => column.columnDef.meta?.stickyLeft)
    .at(-1)?.id;

  const cellLayout = (column: Column<TData, unknown>) => {
    const meta = column.columnDef.meta;
    const sticky: CellSticky | undefined = meta?.stickyLeft
      ? "left"
      : meta?.stickyRight
        ? "right"
        : undefined;
    return {
      $align: meta?.align,
      $sticky: sticky,
      $edge: column.id === stickyLeftEdgeId,
      style: {
        width: column.getSize(),
        ...(sticky === "left" && { left: column.getStart() }),
      },
    };
  };

  return (
    <ScrollContainer ref={scrollRef}>
      <div style={{ minWidth: totalWidth }}>
        <HeaderRow>
          {table.getFlatHeaders().map((header) => {
            const canSort = header.column.getCanSort();
            const sorted = header.column.getIsSorted();
            return (
              <HeaderCell
                key={header.id}
                $sortable={canSort}
                {...cellLayout(header.column)}
                onClick={
                  canSort
                    ? header.column.getToggleSortingHandler()
                    : undefined
                }
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
                {sorted === "asc" && <ChevronUp size={14} />}
                {sorted === "desc" && <ChevronDown size={14} />}
              </HeaderCell>
            );
          })}
        </HeaderRow>

        <div
          style={{
            height: virtualizer.getTotalSize(),
            position: "relative",
          }}
        >
          {virtualItems.map((virtualRow) => {
            const item = items[virtualRow.index];
            if (!item) {
              return null;
            }
            const { row, sub } = item;
            if (sub !== undefined && subRows) {
              return (
                <BodyRow
                  key={`${row.id}:${subRows.getId(sub)}`}
                  $height={virtualRow.size}
                  $translateY={virtualRow.start}
                >
                  {row.getVisibleCells().map((cell) => (
                    <BodyCell key={cell.id} {...cellLayout(cell.column)}>
                      {subRows.renderCell(cell.column.id, row.original, sub)}
                    </BodyCell>
                  ))}
                </BodyRow>
              );
            }
            return (
              <BodyRow
                key={row.id}
                $clickable={!!onRowClick}
                $height={virtualRow.size}
                $translateY={virtualRow.start}
                onClick={
                  onRowClick ? () => onRowClick(row.original) : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <BodyCell key={cell.id} {...cellLayout(cell.column)}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </BodyCell>
                ))}
              </BodyRow>
            );
          })}
        </div>
      </div>
    </ScrollContainer>
  );
}
